import { Head, Link, router } from '@inertiajs/react'
import { FormEvent, useRef, useState } from 'react'
import { ArrowLeft, Camera, User } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface CurrentAgent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface AgentsFormProps {
  agent: Agent | null
  currentAgent: CurrentAgent
}

export default function AgentsForm({ agent, currentAgent }: AgentsFormProps) {
  const isEditing = !!agent
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(agent?.name || '')
  const [email, setEmail] = useState(agent?.email || '')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState(agent?.role || 'agent')
  const [previewUrl, setPreviewUrl] = useState<string | null>(agent?.avatar_url || null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const formData = new FormData()
    formData.append('agent[name]', name)
    formData.append('agent[role]', role)

    if (!isEditing) {
      formData.append('agent[email]', email)
    }

    if (password) {
      formData.append('agent[password]', password)
    }

    if (selectedFile) {
      formData.append('agent[avatar]', selectedFile)
    }

    if (isEditing && agent) {
      router.post(`/agents/${agent.id}`, formData, {
        forceFormData: true,
        headers: { 'X-HTTP-Method-Override': 'PATCH' },
        onFinish: () => setProcessing(false),
      })
    } else {
      router.post('/agents', formData, {
        forceFormData: true,
        onFinish: () => setProcessing(false),
      })
    }
  }

  const isValid = name.trim() && (isEditing || (email.trim() && password.trim()))

  return (
    <>
      <Head title={isEditing ? 'Editar Agente' : 'Novo Agente'} />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/agents"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="agents" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6">
                  {/* Avatar upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Foto do Perfil
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt="Avatar"
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-200">
                            <User className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors cursor-pointer"
                        >
                          <Camera className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
                        >
                          {previewUrl ? 'Alterar foto' : 'Adicionar foto'}
                        </button>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF ou WebP. Max 5MB.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nome
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nome completo"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isEditing}
                        placeholder="email@exemplo.com"
                        className={`w-full px-3 py-2 border rounded-lg outline-none ${
                          isEditing
                            ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                            : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                        }`}
                      />
                      {isEditing && (
                        <p className="mt-1 text-xs text-gray-500">O email não pode ser alterado.</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        Senha {isEditing && <span className="font-normal text-gray-500">(deixe em branco para manter a atual)</span>}
                      </label>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={isEditing ? '••••••••' : 'Senha'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                        Função
                      </label>
                      <select
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                      >
                        <option value="agent">Agente</option>
                        <option value="admin">Administrador</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Administradores podem gerenciar agentes e configurações da organização.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <Link
                      href="/agents"
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </Link>
                    <button
                      type="submit"
                      disabled={processing || !isValid}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {processing ? 'Salvando...' : isEditing ? 'Atualizar Agente' : 'Criar Agente'}
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
